import { compareValues, memoize, uniqueByKey } from "@ls/js-utils";
import { createSelector } from "reselect";
import SelectionState from "state/selections/SelectionState";
import scope from "./scope";

import { selectDisplayBenchmarkValue } from "state/selections/selector";

export const selectAttributeData = (state) => state[scope].attributeData;
export const selectFetchingAttributeData = (state) =>
  state[scope].fetchingAttributeData;

export const selectAccountOrders = createSelector(
  selectAttributeData,
  (groupingResponse) => {
    return (
      [...(groupingResponse?.orders ?? [])]
        .sort(compareValues("order"))
        .map(({ id }) => id) ?? []
    );
  }
);


export const selectGetEntityOrder = createSelector(
  selectAttributeData,
  (groupingResponse) => {
    const orderMap =
      groupingResponse?.orders?.reduce((map, orderInfo) => {
        map[orderInfo.id] = orderInfo.order;
        return map;
      }, {}) ?? {};
    return (entityId) => orderMap[entityId];
  }
);

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return (state) => state[scope]["definitions"][selectionState];
  }

  return createSelector(
    (state) => state[scope]["definitions"][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(
        ({ complete }) => complete
      );
      return completeDefinitions.filter(
        uniqueByKey(completeDefinitions, "key")
      );
    }
  );
});

export const selectFetchingEntityAttributes = (state) =>
  state[scope].fetchingEntityAttributes;
export const selectEntityAttributes = (state) => state[scope].entityAttributes;
export const selectFetchingAttributeValues = (state) =>
  state[scope].fetchingAttributeValues;
export const selectAttributeValues = (state) => state[scope].attributeValues;
export const selectAttributeValuesSelectionOptions = createSelector(
  selectAttributeValues,
  (attributeValues) => {
    if (attributeValues && attributeValues.length > 0) {
      return [
        ...attributeValues.map(({ name }) => ({ label: name, value: name })),
        {
          label: "benchmarkName",
          value: "benchmarkName",
        },
      ].sort(compareValues("label"));
    }
    return [];
  }
);



export const selectRowsMap = createSelector(
  selectAttributeData, selectDisplayBenchmarkValue(SelectionState.UNCOMMITTED),
  (groupingResponse, displayBenchValue) => {
    if (!Array.isArray(groupingResponse?.attributes)) {
      return {};
    }

    const bmkEntityIdSet = new Set(
      groupingResponse?.attributes
        .filter(({ benchmarkId }) => benchmarkId !== undefined)
        .map(({ benchmarkId }) => benchmarkId)
    );
    const accEntityIdSet = new Set(
      groupingResponse?.attributes
        .filter(({ primaryKey }) => primaryKey["entityId"] !== undefined)
        .map(({ primaryKey }) => primaryKey["entityId"])
    );
    const diff = new Set([...bmkEntityIdSet].filter(x => !accEntityIdSet.has(x)));
    return groupingResponse.attributes.reduce((rowsMap, attributes) => {
      const { primaryKey = {} } = attributes;
      const { entityId } = primaryKey;
      rowsMap[entityId] = {
        ...attributes,
        ...primaryKey,
        isBenchmark: !!displayBenchValue ? (bmkEntityIdSet.has(entityId)) : diff.has(entityId) && !attributes.benchmarkId,
        accountCount: groupingResponse.attributes //count the accounts that has this entityId as its bmk
          ?.filter(({ benchmarkId }) => benchmarkId === entityId)?.length,
      };
      return rowsMap;
    }, {});
  }
);
